import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Stack,
    Skeleton,
    CircularProgress,
    Drawer,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Collapse,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import {
    ArrowLeft,
    Check,
    CheckCircle2,
    ClipboardCheck,
    CreditCard,
    Download,
    DollarSign,
    FileText,
    Layers,
    Link2,
    Minus,
    Package,
    Pencil,
    Play,
    Plus,
    RotateCcw,
    Save,
    Send,
    Tag,
    Trash2,
    Wrench,
    X,
} from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { httpClient } from "../../common/httpclient";
import DialogForm from "../../component/dialog/DialogForm";
import PageScaffold from "../../component/pageScaffold/PageScaffold";
import OrderData from "./orderData";
import { formatPhone } from '../../common/formatters';
import PaymentModal from "./paymentmodal/PaymentModal";
import DefaultAutocomplete from "../../component/autocomplete/DefaultAutocomplete";
import { standaloneFieldSx } from "../../common/fieldStyles";
import StatusChip from "../../component/common/StatusChip";
import ChecklistSelectionModal from "../vehicles/checklist/ChecklistSelectionModal";
import ChecklistFillerModal from "../vehicles/checklist/ChecklistFillerModal";
import ChecklistWhatsappDialog from "../../component/dialog/ChecklistWhatsappDialog";
import { downloadServiceOrderPDF } from "./Printer";
import { getEnterpriseIdFromToken, useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileOrderOperator from "../../component/serviceorder/MobileOrderOperator";
import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import { MobileAlertDialog } from '../../component/sale/MobileSaleView';

// ============================================================================
// Componentes locais de apresentação — mesmo vocabulário visual já aprovado em
// `sales/View.js` (cabeçalho em gradiente com ações computadas por status,
// DESIGN-SYSTEM.md §6.3) e `Sale.js` (carrinho POS editável, §5). Não são
// exportados porque cada tela ainda mantém sua própria cópia local — mesmo
// padrão de duplicação intencional já usado entre esses dois arquivos.
// ============================================================================

// Mesmos 5 status reais de `StatusChip domain="orderService"`, mas sempre
// como pill translúcido branco sobre o gradiente (o bg tingido do StatusChip
// fica ilegível ali) — igual ao `HeaderStatusPill` de `sales/View.js`.
function HeaderStatusPill({ status }) {
    const label = status === 'Paga' ? 'Faturada' : (status || 'Não Iniciada');
    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11.5px',
            fontWeight: 700,
            padding: '5px 11px',
            borderRadius: '20px',
            bgcolor: 'rgba(255,255,255,.22)',
            color: '#fff',
        }}>
            {label}
        </Box>
    );
}

// `.os-actions button` — ghost translúcido sobre o gradiente; `solid` = ação
// primária (fundo branco), `danger` = hover destrutivo, `loading` troca o
// ícone por um spinner (usado em "Baixar PDF").
function HeaderActionButton({ icon: Icon, label, onClick, solid, danger, loading, disabled }) {
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            startIcon={loading ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <Icon size={14} strokeWidth={2} />}
            sx={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,.3)',
                bgcolor: solid ? '#fff' : 'rgba(255,255,255,.12)',
                color: solid ? 'brand2.main' : '#fff',
                fontSize: '12.5px',
                fontWeight: 700,
                '&:hover': solid
                    ? { bgcolor: '#fff', filter: 'brightness(.97)' }
                    : danger
                        ? { bgcolor: '#fecaca', color: '#7f1d1d', borderColor: '#fecaca' }
                        : { bgcolor: 'rgba(255,255,255,.22)', borderColor: 'rgba(255,255,255,.3)' },
                '&.Mui-disabled': { color: solid ? 'brand2.main' : 'rgba(255,255,255,.6)', opacity: 0.7 },
            }}
        >
            {label}
        </Button>
    );
}

// `.os-meta-grid .mi` — par label/valor (+ sub-linha opcional) do cabeçalho.
function MetaItem({ label, value, sub }) {
    if (!value) return null;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px', color: '#fff' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#fff' }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,.75)', mt: '2px' }}>{sub}</Typography>}
        </Box>
    );
}

// `.summary-row` — usado no resumo da ordem (aba Itens) e resumo financeiro
// (aba Financeiro).
function SummaryRow({ label, value, tone, big }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.875 }}>
            <Typography sx={{ fontSize: 13, color: tone === 'danger' ? 'error.main' : 'text.secondary' }}>{label}</Typography>
            <Typography sx={{
                fontSize: big ? 17 : 14,
                fontWeight: big ? 800 : 700,
                color: tone === 'danger' ? 'error.main' : tone === 'success' ? 'success.main' : 'text.primary',
            }}>
                {value}
            </Typography>
        </Box>
    );
}

// `.items-empty`/`.checklist-empty`/`.fin-empty` — dashed-circle + texto +
// ação opcional (DESIGN-SYSTEM.md §5.11).
function EmptyMiniState({ icon: Icon, title, subtitle, size = 64, action }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, textAlign: 'center' }}>
            <Box sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                <Icon size={size >= 70 ? 26 : 22} strokeWidth={1.6} />
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: '12px', color: 'text.disabled', maxWidth: 280 }}>{subtitle}</Typography>}
            {action}
        </Box>
    );
}

// Botão redondo +/- do carrinho (`.qty-box button`).
function QtyButton({ tone, onClick, disabled, loading, children, compact }) {
    return (
        <IconButton
            size="small"
            aria-label={tone === 'minus' ? 'Diminuir quantidade' : 'Aumentar quantidade'}
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: compact ? 30 : 26,
                height: compact ? 30 : 26,
                // `&&` vence o `min-width/min-height: 44px` global do MobileLayout.
                ...(compact && { '&&': { minWidth: 30, minHeight: 30 } }),
                borderRadius: compact ? '50%' : '8px',
                color: '#fff',
                bgcolor: tone === 'minus' ? 'error.main' : 'success.main',
                '&:hover': { bgcolor: tone === 'minus' ? 'error.dark' : 'success.dark' },
                '&.Mui-disabled': { opacity: 0.5, color: '#fff' },
            }}
        >
            {loading ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : children}
        </IconButton>
    );
}

// Botão de ícone de linha (editar/desconto/remover) — `.item-btns button`.
function RowIconButton({ onClick, disabled, loading, hoverColor, children, compact }) {
    return (
        <IconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: compact ? 32 : 28,
                height: compact ? 32 : 28,
                // `&&` vence o `min-width/min-height: 44px` global do MobileLayout.
                ...(compact && { '&&': { minWidth: 32, minHeight: 32 } }),
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                color: compact && hoverColor === 'error.main' ? 'error.main' : 'text.disabled',
                '&:hover': { color: hoverColor || 'primary.main', borderColor: hoverColor || 'primary.main' },
            }}
        >
            {loading ? <CircularProgress size={12} /> : children}
        </IconButton>
    );
}

// `.pos-item` — linha do carrinho de itens da ordem, editável ou somente
// leitura conforme `editable` (DESIGN-SYSTEM.md §6.3 `isEditable()`).
function CartRow({
    item,
    editable,
    onIncrement,
    onDecrement,
    onEdit,
    onDiscount,
    onRemove,
    incrementing,
    decrementing,
    deleting,
}) {
    const isService = item.type === 'service';
    const Icon = isService ? Wrench : Package;
    const compact = useIsMobileShell();
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 1 : 1.5,
            p: compact ? '8px 10px' : '13px 10px',
            borderRadius: '12px',
            mb: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
            '&:hover': { bgcolor: 'background.subtle' },
        }}>
            <Box sx={{
                width: compact ? 32 : 42,
                height: compact ? 32 : 42,
                borderRadius: compact ? '9px' : '11px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isService ? 'secondary.bg' : 'info.bg',
                color: isService ? 'secondary.main' : 'primary.main',
            }}>
                <Icon size={compact ? 15 : 18} strokeWidth={1.8} />
            </Box>

            {/* No mobile o título ocupa a 1ª linha e os controles ficam na 2ª. */}
            <Box sx={{ flex: compact ? '1 1 calc(100% - 50px)' : 1, minWidth: compact ? 0 : 160 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary' }}>{item.name}</Typography>
                    <StatusChip domain="itemType" value={isService ? 'servico' : 'produto'} />
                </Box>
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: '2px' }}>
                    R$ {item.amount_unit} · un.
                </Typography>
                {item.type === 'product' && item.item_discount_type ? (
                    <Typography sx={{ fontSize: 11, color: 'error.main', mt: '2px' }}>
                        Desconto: {item.item_discount_type}
                        {item.item_discount_amount ? ` (− R$ ${item.item_discount_amount})` : ''}
                    </Typography>
                ) : null}
            </Box>

            {editable ? (
                <>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexShrink: 0,
                        ...(compact && { gap: 0.25, p: '2px', border: '1px solid', borderColor: 'divider', borderRadius: '999px' }),
                    }}>
                        <QtyButton compact={compact} tone="minus" onClick={onDecrement} disabled={decrementing} loading={decrementing}>
                            <Minus size={compact ? 14 : 13} strokeWidth={2.5} />
                        </QtyButton>
                        <Typography sx={{ width: compact ? 26 : 22, textAlign: 'center', fontWeight: 800, fontSize: 13 }}>
                            {item.quantity}
                        </Typography>
                        <QtyButton compact={compact} tone="plus" onClick={onIncrement} disabled={incrementing} loading={incrementing}>
                            <Plus size={compact ? 14 : 13} strokeWidth={2.5} />
                        </QtyButton>
                    </Box>

                    <Typography sx={{
                        width: 84,
                        textAlign: 'right',
                        fontWeight: 800,
                        fontSize: 13.5,
                        flexShrink: 0,
                        ...(compact && { width: 'auto', flex: 1, minWidth: 0, flexShrink: 1 }),
                    }}>
                        R$ {item.amount_total}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: compact ? 0.75 : 0.5, flexShrink: 0 }}>
                        <Tooltip title="Editar item">
                            <span><RowIconButton compact={compact} onClick={onEdit}><Pencil size={compact ? 16 : 13} /></RowIconButton></span>
                        </Tooltip>
                        {item.type === 'product' && (
                            <Tooltip title="Desconto no produto (linha)">
                                <span>
                                    <RowIconButton compact={compact} onClick={onDiscount} hoverColor="secondary.main">
                                        <Tag size={compact ? 16 : 13} />
                                    </RowIconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Remover item">
                            <span>
                                <RowIconButton compact={compact} onClick={onRemove} disabled={deleting} loading={deleting} hoverColor="error.main">
                                    <Trash2 size={compact ? 16 : 13} />
                                </RowIconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </>
            ) : (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flexShrink: 0 }}>
                    Qtd: {item.quantity} · R$ {item.amount_total}
                </Typography>
            )}
        </Box>
    );
}

// `.paid-row` — uma forma de pagamento registrada (aba Financeiro), mesmo
// mapeamento de cor/ícone de `sales/View.js` `PAYMENT_META`.
const PAYMENT_META = {
    'CRÉDITO': { icon: CreditCard, iconBg: 'secondary.bg', iconFg: 'secondary.main', label: 'Cartão de crédito' },
    'DÉBITO': { icon: CreditCard, iconBg: 'info.bg', iconFg: 'primary.main', label: 'Cartão de débito' },
    'PIXKEY': { icon: DollarSign, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX - Chave' },
    'PIXQRCODE': { icon: DollarSign, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX - QR Code' },
    'DINHEIRO': { icon: DollarSign, iconBg: 'success.bg', iconFg: 'success.main', label: 'Dinheiro' },
};

function PaymentRow({ payment }) {
    const meta = PAYMENT_META[payment.method] || { icon: CreditCard, iconBg: 'background.subtle', iconFg: 'text.disabled', label: payment.method };
    const Icon = meta.icon;
    let extra = null;
    if (payment.method === 'CRÉDITO' && payment.NumberInstallments > 1) {
        extra = `${payment.NumberInstallments}x de R$ ${payment.ValueInstallment}`;
    } else if (payment.method === 'DINHEIRO' && payment.CashChange && payment.CashChange !== '0,00') {
        extra = `Troco: R$ ${payment.CashChange}`;
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '13px', bgcolor: 'background.subtle', borderRadius: '12px', mb: 1, '&:last-of-type': { mb: 0 } }}>
            <Box sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: meta.iconBg,
                color: meta.iconFg,
            }}>
                <Icon size={16} strokeWidth={2} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{meta.label}</Typography>
                {extra && <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{extra}</Typography>}
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>R$ {payment.amount}</Typography>
        </Box>
    );
}

// Guarda contra a data "zero" (`0001-01-01`) retornada quando a ordem não
// tem data de entrega definida — mesmo tratamento já usado em `List.js`.
function formatOrderDateTime(value) {
    if (!value) return null;
    const formatted = new Date(value).toLocaleString('pt-BR', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
    if (formatted === '31/12/1, 23:59' || formatted === '31/12/0001, 23:59') return null;
    return formatted;
}

export default function Operator() {
    const isMobileShell = useIsMobileShell();
    const navigate = useNavigate();
    const { settings } = useEnterpriseConfigContext();
    const { hasPermission } = usePermissions();
    const [message, setMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [edit, setEdit] = useState(false);
    const [isForm, setIsForm] = useState(false);
    const orderDataRef = useRef();
    const { id } = useParams();
    const [orderId, setOrderId] = useState('');
    const [orderNumber, setOrderNumber] = useState('');
    const [wasUpdated, setWasUpdated] = useState(false);
    const [openDialogConfirm, setOpenDialogConfirm] = useState(false);
    const [messageDialogConfirm, setMessageDialogConfirm] = useState('');
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelReasonError, setCancelReasonError] = useState('');
    const [loadingCancel, setLoadingCancel] = useState(false);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [severity, setSeverity] = useState('success');

    // Mobile (operar/faturar OS) não tem toasts: sucesso/info são descartados (feedback visual na própria UI)
    // e só erro/aviso abre o modal "Atenção". Um único estado => erros em sequência substituem a mensagem.
    const [mobileAlert, setMobileAlert] = useState({ open: false, message: '' });
    const closeMobileAlert = () => setMobileAlert((prev) => ({ ...prev, open: false }));

    // Abas Itens/Financeiro/Checklist (DESIGN-SYSTEM.md §6.3) + filtro
    // Todos/Produtos/Serviços dentro da aba Itens.
    const [activeTab, setActiveTab] = useState('itens');
    const [itemFilter, setItemFilter] = useState('todos');

    // Estados para modais de checklist
    const [openChecklistSelectionModal, setOpenChecklistSelectionModal] = useState(false);
    const [openChecklistFillerModal, setOpenChecklistFillerModal] = useState(false);
    const [selectedChecklist, setSelectedChecklist] = useState(null);
    const [shouldStartOrderAfterChecklist, setShouldStartOrderAfterChecklist] = useState(false);

    // Estado/leitura do checklist já preenchido (aba Checklist).
    const [checklistData, setChecklistData] = useState(null);
    const [loadingChecklist, setLoadingChecklist] = useState(true);
    const [downloadingChecklistPDF, setDownloadingChecklistPDF] = useState(false);
    const [checklistWhatsappOpen, setChecklistWhatsappOpen] = useState(false);

    // Resumo financeiro + pagamentos (aba Financeiro).
    const [financeState, setFinanceState] = useState({ subtotal: '0,00', discount: '0,00', total: '0,00', total_liquid: '0,00' });
    const [loadingFinance, setLoadingFinance] = useState(true);
    const [payments, setPayments] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(true);

    const [downloadingOrderPDF, setDownloadingOrderPDF] = useState(false);

    // Estados para modal de edição de item
    const [openEditItemModal, setOpenEditItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemName, setItemName] = useState('');
    const [itemValue, setItemValue] = useState('');
    const [itemQuantity, setItemQuantity] = useState(1);
    const [editItemDiscountValue, setEditItemDiscountValue] = useState('');
    const [itemDiscountDialog, setItemDiscountDialog] = useState({
        open: false,
        item: null,
        value: '',
        loading: false
    });
    const [orderState, setOrderState] = useState({
        id: '',
        total: '0,00',
        totalItems: 0,
        total_services: '0,00',
        total_products: '0,00',
        total_product_item_discount: '0,00',
        cart: [],
        productList: [],
        total_discount: '0,00',
        subtotal: '0,00',
        remaining_value: '0,00',
        reason: '',
        number: 0,
        cash_register_name: '',
        status: 'Não Iniciada'
    });

    const [orderDetails, setOrderDetails] = useState({
        customer_name: '',
        customer_contact: '',
        vehicle_name: '',
        vehicle_plate: '',
        vehicle_type: '',
        order_date: null,
        delivery_date: null,
        observations: '',
        defect_reason: '',
        cancel_reason: '',
        budget_id: null,
        budget_number: null,
        technician_name: '',
        vehicle_kilometer: null,
    });
    const [uiState, setUiState] = useState({
        searchProduct: '',
        fieldSearchFocus: true,
        autocompleteResetKey: 0,  // Chave para forçar reset do autocomplete
        showAttention: false,
        attentionMessage: '',
    });

    // Mobile: o snackbar da tela vira modal (só erro/aviso) e é sempre fechado.
    useEffect(() => {
        if (!isMobileShell || !openSnackbar) return;
        if (severity === 'error' || severity === 'warning') {
            setMobileAlert({ open: true, message: message || 'Ocorreu um erro. Tente novamente.' });
        }
        setOpenSnackbar(false);
    }, [isMobileShell, openSnackbar, severity, message]);

    // Mobile: erro de item (antes banner inline "attention") também abre o modal.
    useEffect(() => {
        if (!isMobileShell || !uiState.showAttention) return;
        setMobileAlert({ open: true, message: uiState.attentionMessage || 'Ocorreu um erro. Tente novamente.' });
        setUiState((prev) => ({ ...prev, showAttention: false }));
    }, [isMobileShell, uiState.showAttention, uiState.attentionMessage]);

    // Estados para loading moderno ao adicionar item
    const [loadingState, setLoadingState] = useState({
        isAddingItem: false,
        addingItemName: '',
        progress: 0,
        showProgress: false,
        showSuccess: false
    });

    // Estado para controlar loading individual de cada item
    const [itemLoadingStates, setItemLoadingStates] = useState({});

    // Estados de loading para skeleton
    const [loading, setLoading] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingValues, setLoadingValues] = useState(true);
    const [itemsAutocomplete, setItemsAutocomplete] = useState([]);
    const [loadingConfirmAction, setLoadingConfirmAction] = useState(false);

    const handleSearchItem = async (code) => {
        if (!code) return;

        const response = await httpClient.get(`/orderservices/searchitem?filter=${code}`);
        setItemsAutocomplete(response.data);
        return response.data;
    };

    // Funções auxiliares para gerenciar loading individual dos itens
    const setItemLoading = (itemId, operation, isLoading) => {
        setItemLoadingStates(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                [operation]: isLoading
            }
        }));
    };

    const isItemLoading = (itemId, operation) => {
        return itemLoadingStates[itemId]?.[operation] || false;
    };

    const addToCart = async (item, quantity = 1) => {

        // Inicia o loading
        setLoadingState({
            isAddingItem: true,
            addingItemName: item.name,
            progress: 0,
            showProgress: true
        });

        // Simula progresso para melhor UX
        const progressInterval = setInterval(() => {
            setLoadingState(prev => ({
                ...prev,
                progress: Math.min(prev.progress + 15, 90)
            }));
        }, 100);

        try {
            const existingItem = orderState.cart.find(i => i.id === item.id);
            if (existingItem) {
                return await updateExistingCartItem(existingItem, item, quantity);
            } else {
                setUiState(prev => ({ ...prev, showAttention: false }));
                return await addNewCartItem(item, quantity);
            }
        } finally {
            clearInterval(progressInterval);
            // Finaliza o progresso
            setLoadingState(prev => ({
                ...prev,
                progress: 100,
                showProgress: false,
                showSuccess: true
            }));

            // Remove o loading após um breve delay
            setTimeout(() => {
                setLoadingState({
                    isAddingItem: false,
                    addingItemName: '',
                    progress: 0,
                    showProgress: false,
                    showSuccess: false
                });
            }, 500);
        }
    };

    const addNewCartItem = async (item, quantity) => {
        item.quantity = quantity;

        const newItem = {
            id: item.id,
            name: item.name,
            amount_unit: item.value,
            quantity: quantity,
            total: item.value,
            amount_total: item.value,
            is_generic: item.is_generic,
            type: item.type,
            order_service_id: orderId
        };
        let response;
        if (item.is_generic) {
            response = await httpClient.post(`/orderservices/add/genericItem`, newItem);
        } else if (item.type === 'product') {
            response = await httpClient.post(`/orderservices/add/product`, newItem);
        } else {
            response = await httpClient.post(`/orderservices/add/service`, newItem);
        }

        if (response.data.error) {
            setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
            return;
        } else {
            // Atualizar o item com os dados retornados da API se disponíveis
            const updatedNewItem = {
                ...newItem,
                amount_unit: response.data.amount_unit_item,
                amount_total: response.data.amount_total_item
            };

            setOrderState(prev => ({
                ...prev,
                cart: [...prev.cart, updatedNewItem],
                total: response.data.amount,
                totalItems: response.data.count_items,
                total_products: response.data.amount_products,
                total_services: response.data.amount_services
            }));
            fetchValues();
        }

        setUiState(prev => ({ ...prev, showAttention: false }));

        return;
    };

    const updateExistingCartItem = async (existingItem, item, quantity) => {
        item.quantity = existingItem.quantity + quantity;
        item.order_service_id = orderId;

        let response;
        if (item.is_generic) {
            const existingGenericItem = orderState.cart.find(i => i.id === item.id && item.is_generic);
            if (existingGenericItem) {
                response = await httpClient.put(`/orderservices/update/genericItem`, existingGenericItem);
            } else {
                response = await httpClient.post(`/orderservices/add/genericItem`, item);
            }
        } else {
            // Para itens não genéricos, usar POST para adicionar mais uma unidade
            const updateData = {
                id: item.id,
                order_service_id: orderId,
                quantity: 1,
                type_item: item.type,
            };

            if (item.type === 'product') {
                response = await httpClient.put(`/orderservices/update/product`, updateData);
            } else {
                response = await httpClient.put(`/orderservices/update/service`, updateData);
            }
            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }
        }

        // Atualização única do estado após a resposta da API
        if (response && response.data && !response.data.error) {
            const updatedCart = orderState.cart.map(i =>
                i.id === item.id
                    ? {
                        ...i,
                        quantity: item.quantity,
                        amount_total: response.data.amount_total_item,
                        amount_unit: response.data.amount_unit_item
                    }
                    : i
            );

            setOrderState(prev => ({
                ...prev,
                cart: updatedCart,
                total: response.data.amount,
                totalItems: response.data.count_items,
                total_products: response.data.amount_products,
                total_services: response.data.amount_services
            }));
            fetchValues();
        }

        return response;
    };

    const handleDecrementItem = async (itemId) => {
        const item = orderState.cart.find(i => i.id === itemId);
        if (!item) return;

        // Inicia o loading individual do item
        setItemLoading(itemId, 'decrement', true);

        try {
            let response;
            response = await httpClient.post(`/orderservices/${orderId}/item/${itemId}/decrement`);
            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }
            const updatedCart = orderState.cart.map(i =>
                i.id === item.id
                    ? {
                        ...i,
                        quantity: item.quantity - 1,
                        amount_total: response.data.amount_total_item,
                        amount_unit: response.data.amount_unit_item
                    }
                    : i
            );
            setOrderState(prev => ({
                ...prev,
                cart: updatedCart,
                totalItems: prev.totalItems - 1
            }));

            fetchValues();
        } finally {
            // Finaliza o loading individual do item
            setItemLoading(itemId, 'decrement', false);
        }
    };

    const handleIncrementItem = async (itemId) => {
        const item = orderState.cart.find(i => i.id === itemId);
        if (!item) return;

        // Inicia o loading individual do item
        setItemLoading(itemId, 'increment', true);

        try {
            // Implementar lógica de incremento aqui
            let response;
            response = await httpClient.post(`/orderservices/${orderId}/item/${itemId}/increment`);
            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                setSeverity('error');
                setOpenSnackbar(true);
                setMessage(response.data.error);
                return;
            }
            const updatedCart = orderState.cart.map(i =>
                i.id === item.id
                    ? {
                        ...i,
                        quantity: item.quantity + 1,
                        amount_total: response.data.amount_total_item,
                        amount_unit: response.data.amount_unit_item
                    }
                    : i
            );
            setOrderState(prev => ({
                ...prev,
                cart: updatedCart,
                totalItems: prev.totalItems + 1
            }));

            fetchValues();
        } finally {
            // Finaliza o loading individual do item
            setItemLoading(itemId, 'increment', false);
        }
    };

    const handleDeleteItem = async (itemId) => {
        const item = orderState.cart.find(i => i.id === itemId);
        if (!item) return;

        // Inicia o loading individual do item
        setItemLoading(itemId, 'delete', true);

        try {
            // Implementar lógica de remoção aqui
            var data = {
                item_id: itemId,
                order_service_id: orderId,
                type_item: item.type,
                reverse_quantity: item.quantity
            }

            await httpClient.post(`/orderservices/${orderId}/item/remove`, data);

            // Atualizar o carrinho removendo o item
            setOrderState(prev => ({
                ...prev,
                cart: prev.cart.filter(i => i.id !== itemId),
                totalItems: prev.totalItems - item.quantity
            }));

            // Recarregar os valores totais
            await fetchValues();

        } catch (error) {
            console.error('Erro ao remover item:', error);
            setMessage('Erro ao remover item da lista');
            setOpenSnackbar(true);
            setSeverity('error');
        } finally {
            // Finaliza o loading individual do item
            setItemLoading(itemId, 'delete', false);
        }
    };

    // Funções para gerenciar o modal de edição de item
    const handleOpenEditItemModal = (item) => {
        setEditingItem(item);
        setItemName(item.name || item.item_name);
        setItemValue(item.amount_unit || item.value);
        setItemQuantity(item.quantity);
        setEditItemDiscountValue(item.item_discount_type || '');
        setOpenEditItemModal(true);
    };

    const handleCloseEditItemModal = () => {
        setOpenEditItemModal(false);
        setEditingItem(null);
        setItemName('');
        setItemValue('');
        setItemQuantity(1);
        setEditItemDiscountValue('');
    };

    const openProductItemDiscountDialog = (item) => {
        if (!item || item.type !== 'product') return;
        setItemDiscountDialog({
            open: true,
            item,
            value: item.item_discount_type || '',
            loading: false
        });
    };

    const handleSubmitProductItemDiscount = async (removeOnly) => {
        const { item } = itemDiscountDialog;
        if (!item || !id) return;
        setItemDiscountDialog((prev) => ({ ...prev, loading: true }));
        try {
            const value = removeOnly ? '' : itemDiscountDialog.value.trim();
            if (!removeOnly && !value) {
                setMessage('Informe o desconto (ex.: 10% ou 10,00) ou use Remover.');
                setSeverity('error');
                setOpenSnackbar(true);
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            const response = await httpClient.post(`/orderservices/${id}/item/product/discount`, {
                product_id: item.id,
                is_generic: Boolean(item.is_generic),
                product_name: item.is_generic ? (item.name || item.item_name || '').trim() : '',
                value
            });
            const status = response?.status ?? 0;
            if (status < 200 || status >= 300) {
                const msg =
                    messageFromApiErrorBody(response.data) ||
                    'Erro ao aplicar desconto no produto';
                setMessage(msg);
                setSeverity('error');
                setOpenSnackbar(true);
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            if (response.data && typeof response.data.error === 'string' && response.data.error) {
                setMessage(response.data.error);
                setSeverity('error');
                setOpenSnackbar(true);
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            await fetchItems();
            await fetchValues();
            setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
        } catch (error) {
            const msg =
                messageFromApiErrorBody(error.response?.data) ||
                error.message ||
                'Erro ao aplicar desconto no produto';
            setMessage(msg);
            setSeverity('error');
            setOpenSnackbar(true);
            setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleSaveItem = async () => {
        if (!editingItem || !itemName.trim() || !itemValue || !itemQuantity) {
            setMessage('Nome, valor e quantidade são obrigatórios');
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }

        try {
            const updatedItem = {
                ...editingItem,
                name: itemName.trim(),
                amount_unit: itemValue,
                quantity: parseInt(itemQuantity),
                amount_total: itemValue,
                order_service_id: orderId,
                edit_quantity: true
            };

            let response;

            if (editingItem.is_generic) {
                // Para itens genéricos, usar a API específica
                updatedItem.value = itemValue;
                if (editingItem.type === 'product' && editItemDiscountValue.trim()) {
                    updatedItem.item_discount_value = editItemDiscountValue.trim();
                }
                response = await httpClient.put(`/orderservices/update/genericItem`, updatedItem);
            } else {
                // Para itens não genéricos, usar a API específica do tipo
                const updateData = {
                    id: editingItem.id,
                    order_service_id: orderId,
                    quantity: parseInt(itemQuantity),
                    type_item: editingItem.type,
                    edit_quantity: true
                };
                if (editingItem.type === 'product') {
                    updateData.value = '';
                    if (editItemDiscountValue.trim()) {
                        updateData.item_discount_value = editItemDiscountValue.trim();
                    }
                    response = await httpClient.put(`/orderservices/update/product`, updateData);
                } else {
                    response = await httpClient.put(`/orderservices/update/service`, updateData);
                }
            }

            if (response.data.error) {
                setMessage(response.data.error);
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            // Atualizar o item no carrinho
            setOrderState(prev => ({
                ...prev,
                cart: prev.cart.map(i =>
                    i.id === editingItem.id
                        ? {
                            ...i,
                            name: itemName.trim(),
                            amount_unit: itemValue,
                            quantity: itemQuantity,
                            amount_total: response.data.amount_total_item
                        }
                        : i
                )
            }));

            // Atualizar valores totais e linhas (desconto por produto)
            await fetchValues();
            await fetchItems();

            handleCloseEditItemModal();
            setMessage('Item atualizado com sucesso');
            setSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erro ao atualizar item:', error);
            setMessage('Erro ao atualizar item');
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleEdit = () => {
        setIsForm(true);
        setEdit(true);
        setOrderId(id);
    }

    const closeOrderDataSheet = () => {
        setIsForm(false);
        setEdit(false);
        // Manter orderId alinhado à rota: limpar com '' quebrava add/increment/decrement após salvar.
        setOrderId(id ?? '');
    }

    const handleSave = () => {
        if (orderDataRef && orderDataRef.current) {
            orderDataRef.current.submitForm();
        }
    }

    const handleSubmit = (values) => {
        httpClient.put(`/orderservices/${id}`, values).then(async (response) => {
            if (response.status === 204) {
                setMessage('Ordem de serviço salva com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                // Recarrega dados no pai: fechar o sheet desmonta OrderData antes de wasUpdated
                // disparar fetchOrderData no filho, então a UI principal ficaria desatualizada.
                await fetchOrderBasicData(id);
                closeOrderDataSheet();
                setWasUpdated(true);
            } else if (isMobileShell) {
                setMessage(
                    (typeof response.data === 'string' && response.data.trim())
                    || messageFromApiErrorBody(response.data)
                    || 'Erro ao salvar ordem de serviço'
                );
                setSeverity('error');
                setOpenSnackbar(true);
            }
        }).catch((error) => {
            if (!isMobileShell) throw error;
            setMessage(messageFromApiErrorBody(error.response?.data) || error.message || 'Erro ao salvar ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        });
    };

    const handleGetSelected = (item) => {
        if (!item) return;
        setUiState(prev => ({
            ...prev,
            searchProduct: '',
            autocompleteResetKey: prev.autocompleteResetKey + 1,
            fieldSearchFocus: true // Mantém o foco no campo
        }));
        addToCart(item, 1);
    }

    useEffect(() => {
        if (id) {
            setOrderId(id);
        }
    }, [id]);

    const handleExecuteOrder = () => {
        handleStartOrderAfterChecklist();
    }

    const handleStartOrderAfterChecklist = async () => {
        // Inicia a ordem de serviço após selecionar o checklist
        try {
            const response = await httpClient.post(`/orderservices/${id}/execute`);
            if (response.status === 200) {
                setMessage('Ordem de serviço iniciada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOrderState(prev => ({ ...prev, status: 'Em Execução' }));
            } else if (response.status === 422) {
                setMessage('Erro ao iniciar ordem de serviço');
                setOpenSnackbar(true);
                setSeverity('error');
            }
        } catch (error) {
            setMessage('Erro ao iniciar ordem de serviço');
            setOpenSnackbar(true);
            setSeverity('error');
        }
    }

    const handleSelectChecklist = (checklist) => {
        setSelectedChecklist(checklist);
        setOpenChecklistSelectionModal(false);
        setOpenChecklistFillerModal(true);
    }

    const handleChecklistFillerClose = () => {
        setOpenChecklistFillerModal(false);
        setSelectedChecklist(null);
        setShouldStartOrderAfterChecklist(false);
    }

    const handleChecklistFillerSuccess = () => {
        // Após preencher o checklist com sucesso, inicia a ordem apenas se foi aberto via "Iniciar Execução"
        if (shouldStartOrderAfterChecklist) {
            handleStartOrderAfterChecklist();
        }
        setShouldStartOrderAfterChecklist(false);
        // Atualiza a aba Checklist com o resultado recém-preenchido.
        if (id) {
            fetchChecklist(id);
        }
    }

    const handleOpenChecklistModal = () => {
        setShouldStartOrderAfterChecklist(false);
        setOpenChecklistSelectionModal(true);
    }

    const handleOpenCancelDialog = () => {
        setCancelReason('');
        setCancelReasonError('');
        setOpenCancelDialog(true);
    };

    const handleCancelOrder = async () => {
        const reason = cancelReason.trim();
        if (!reason) {
            setCancelReasonError('Informe o motivo do cancelamento.');
            return;
        }
        setCancelReasonError('');
        setLoadingCancel(true);
        try {
            const response = await httpClient.put(`/orderservices/cancel/${id}`, { reason });
            if (response.status === 200) {
                setMessage('Ordem de serviço cancelada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOrderState(prev => ({ ...prev, status: 'Cancelada' }));
                setOpenCancelDialog(false);
                fetchOrderBasicData(id);
            } else {
                // validateStatus do httpClient aceita 4xx/5xx: o erro vem em response.data.
                setCancelReasonError(
                    (typeof response.data === 'string' && response.data.trim())
                    || messageFromApiErrorBody(response.data)
                    || 'Erro ao cancelar ordem de serviço'
                );
            }
        } catch (error) {
            setCancelReasonError('Erro ao cancelar ordem de serviço');
        } finally {
            setLoadingCancel(false);
        }
    };

    const handleFinalizeOrder = async () => {
        try {
            const response = await httpClient.post(`/orderservices/${id}/finish`);
            if (response.status === 200) {
                setMessage('Ordem de serviço finalizada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOrderState(prev => ({ ...prev, status: 'Finalizada' }));
                return true;
            } else if (response.status === 422) {
                var errMessage = response.data.error;
                setMessage(errMessage);
                setOpenSnackbar(true);
                setSeverity('error');
            } else if (isMobileShell) {
                // httpClient aceita 4xx/5xx: no mobile, qualquer outra falha também precisa de modal.
                setMessage(
                    (typeof response.data === 'string' && response.data.trim())
                    || messageFromApiErrorBody(response.data)
                    || 'Erro ao finalizar ordem de serviço'
                );
                setSeverity('error');
                setOpenSnackbar(true);
            }
        } catch (error) {
            setMessage('Erro ao finalizar ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        }
        return false;
    };

    const handleConfirmAction = () => {
        setLoadingConfirmAction(true);
        if (confirmationAction === 'finalize') {
            handleFinalizeOrder().then(() => {
                setLoadingConfirmAction(false);
                setOpenDialogConfirm(false);
                setConfirmationAction(null);
            });
        }
    };

    const handleOpenPaymentModal = () => {
        setOpenPaymentModal(true);
    };

    const handleClosePaymentModal = () => {
        setOpenPaymentModal(false);
    };

    const handlePaymentFinish = () => {
        setMessage('Ordem de serviço faturada com sucesso!');
        setSeverity('success');
        setOpenSnackbar(true);
        setOpenPaymentModal(false);
        // Atualizar o status da ordem para "Faturada" se necessário
        setOrderState(prev => ({ ...prev, status: 'Faturada' }));
        // Faturada: o voltar (app bar/sistema) passa a ir para a lista.
        if (id) navigate(`/serviceorder/${id}/operator`, { replace: true, state: { id, fromFlow: true } });
        // Aba Financeiro precisa refletir o pagamento recém-registrado.
        if (id) {
            fetchFinanceResume(id);
            fetchPayments(id);
        }
    };

    const handleDownloadOrderPDF = async () => {
        const osId = orderId || id;
        if (!osId) return;
        try {
            setDownloadingOrderPDF(true);
            await downloadServiceOrderPDF(osId, false);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            setMessage('Erro ao gerar PDF da ordem de serviço');
            setOpenSnackbar(true);
            setSeverity('error');
        } finally {
            setDownloadingOrderPDF(false);
        }
    };

    const downloadChecklistPDF = async () => {
        if (!id || !checklistData) return;
        const checklistId = checklistData.checklist_id || checklistData.id;
        if (!checklistId) return;
        try {
            setDownloadingChecklistPDF(true);
            const response = await httpClient.get(`/checklist/service-order/pdf`, {
                params: { checklistId, orderServiceId: id },
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlBlob;
            link.download = `checklist_os_${orderNumber || id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);
        } catch (error) {
            console.error('Erro ao baixar PDF do checklist:', error);
            setMessage('Erro ao baixar PDF do checklist');
            setOpenSnackbar(true);
            setSeverity('error');
        } finally {
            setDownloadingChecklistPDF(false);
        }
    };

    const fetchItems = async () => {
        try {
            setLoadingItems(true);
            const response = await httpClient.get(`/orderservices/${id}/products`);
            const responseServices = await httpClient.get(`/orderservices/${id}/services`);

            const productsWithType = response.data.map((item) => {
                const lineId = item.id || item.product_id || item.item_id || item.order_product_id || item.order_service_product_id;
                return {
                    ...item,
                    id: lineId,
                    name: item.name || item.item_name,
                    type: 'product',
                    value: item.value,
                    total: item.total,
                    is_generic: item.is_generic,
                    item_discount_type: item.item_discount_type ?? '',
                    item_discount_amount: item.item_discount_amount ?? ''
                };
            });

            const servicesWithType = responseServices.data.map((item) => ({
                ...item,
                id: item.id || item.service_id || item.item_id || item.order_service_id || item.order_service_service_id,
                type: 'service',
                value: item.value,
                total: item.total,
            }));

            setOrderState(prev => ({ ...prev, cart: [...productsWithType, ...servicesWithType] }));
        } catch (error) {
            console.error('Erro ao carregar itens:', error);
        } finally {
            setLoadingItems(false);
        }
    }

    const fetchValues = async () => {
        try {
            setLoadingValues(true);
            const response = await httpClient.get(`/orderservices/${id}/values`);

            setOrderState(prev => ({
                ...prev,
                total: response.data.amount,
                totalItems: response.data.count_items,
                total_products: response.data.amount_products,
                total_services: response.data.amount_services,
                total_product_item_discount: response.data.total_product_item_discount ?? prev.total_product_item_discount ?? '0,00'
            }));
            // Mobile: o resumo (Subtotal/Desconto/Total) vem do resumo financeiro; mantém-no em dia a cada mudança nos itens.
            if (isMobileShell) fetchFinanceResume(id);
        } catch (error) {
            console.error('Erro ao carregar valores:', error);
        } finally {
            setLoadingValues(false);
        }
    }

    const fetchFinanceResume = async (orderIdParam) => {
        try {
            setLoadingFinance(true);
            const response = await httpClient.get(`/orderservices/${orderIdParam}/finance-resume`);
            setFinanceState({
                subtotal: response.data.subtotal ?? '0,00',
                discount: response.data.discount ?? '0,00',
                total: response.data.total ?? '0,00',
                total_liquid: response.data.total_liquid ?? '0,00',
            });
        } catch (error) {
            console.error('Erro ao carregar resumo financeiro:', error);
        } finally {
            setLoadingFinance(false);
        }
    };

    const fetchPayments = async (orderIdParam) => {
        try {
            setLoadingPayments(true);
            const response = await httpClient.get(`/orderservices/${orderIdParam}/payments`);
            setPayments(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar pagamentos:', error);
        } finally {
            setLoadingPayments(false);
        }
    };

    const fetchChecklist = async (orderIdParam) => {
        try {
            setLoadingChecklist(true);
            const response = await httpClient.get(`/checklist/service-order/${orderIdParam}`);
            setChecklistData(response.status === 200 && response.data ? response.data : null);
        } catch (error) {
            // Se não encontrar checklist preenchido, mantém o estado vazio.
            setChecklistData(null);
        } finally {
            setLoadingChecklist(false);
        }
    };

    const fetchOrderBasicData = async (orderIdParam) => {
        try {
            const response = await httpClient.get(`/orderservices/${orderIdParam}`);
            if (response.status === 200) {
                setOrderState(prev => ({
                    ...prev,
                    id: response.data.order.id,
                    number: response.data.order.number,
                    status: response.data.order.status,
                    reason: response.data.order.reason || '',
                    cash_register_name: response.data.order.cash_register_name || ''
                }));
                setOrderNumber(response.data.order.number || '');
                setOrderDetails({
                    customer_name: response.data.order?.customer_name || '',
                    customer_contact: response.data.order?.customer_contact || '',
                    vehicle_name: response.data.order?.vehicle_name || '',
                    vehicle_plate: response.data.order?.vehicle_plate || '',
                    vehicle_type: response.data.order?.vehicle_type || '',
                    order_date: response.data.order?.order_date || null,
                    delivery_date: response.data.order?.delivery_date || null,
                    observations: response.data.order?.observations || '',
                    defect_reason: response.data.order?.defect_reason || '',
                    cancel_reason: response.data.order?.cancel_reason || '',
                    budget_id: response.data.order?.budget_id || null,
                    budget_number: response.data.order?.budget_number || null,
                    technician_name: response.data.order?.technician_name || '',
                    vehicle_kilometer: response.data.order?.vehicle_kilometer ?? null,
                });
            }
        } catch (error) {
            console.error('Erro ao carregar dados básicos da ordem:', error);
        }
    }

    useEffect(() => {
        if (id) {
            setLoading(true);
            const promises = [
                fetchOrderBasicData(id),
                fetchItems(),
                fetchValues(),
                fetchFinanceResume(id),
                fetchPayments(id),
            ];
            if (settings?.os_use_checklist) {
                promises.push(fetchChecklist(id));
            } else {
                setLoadingChecklist(false);
            }
            Promise.all(promises).finally(() => {
                setLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, settings?.os_use_checklist]);

    const breadcrumbs = [
        { title: 'Ordens de Serviço', link: '/serviceorders' },
    ];
    const isOrderCancelled = orderState.status === 'Cancelada';
    const canEditOrder = hasPermission('serviceorder.update') && !isOrderCancelled;
    const canFillChecklist = hasPermission('serviceorder.checklist.fill');
    const isNaoIniciada = orderState.status === 'Não Iniciada';
    const isEmExecucao = orderState.status === 'Em Execução';
    const isFinalizada = orderState.status === 'Finalizada';
    const isEditableStatus = isNaoIniciada || isEmExecucao;

    // Ações do cabeçalho computadas a partir do status atual — nunca uma
    // cadeia de condicionais espalhada pelo render (DESIGN-SYSTEM.md §6.3).
    // Finalizada: Copiar link, Enviar por WhatsApp e Reabrir (mesmas ações da View.js).
    const canSendWhatsapp = hasPermission('serviceorder.send');
    const [whatsappConnected, setWhatsappConnected] = useState(false);
    const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
    const [whatsappPhone, setWhatsappPhone] = useState('');
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [loadingWhatsappPhone, setLoadingWhatsappPhone] = useState(false);
    const [openReopenDialog, setOpenReopenDialog] = useState(false);
    const [reopeningOrder, setReopeningOrder] = useState(false);

    useEffect(() => {
        if (!canSendWhatsapp) { setWhatsappConnected(false); return undefined; }
        let active = true;
        httpClient.get('/whatsapp')
            .then((response) => { if (active) setWhatsappConnected(response.data?.status === 'connected'); })
            .catch(() => { if (active) setWhatsappConnected(false); });
        return () => { active = false; };
    }, [canSendWhatsapp]);

    const handleOpenWhatsappSend = async () => {
        setWhatsappPhone('');
        setWhatsappDialogOpen(true);
        try {
            setLoadingWhatsappPhone(true);
            const response = await httpClient.get(`/orderservices/${id}/whatsapp/recipient`);
            setWhatsappPhone(formatPhone(response.data?.phone || ''));
        } catch (error) {
            console.error('Erro ao buscar celular do cliente:', error); // usuário ainda pode digitar o número
        } finally {
            setLoadingWhatsappPhone(false);
        }
    };

    const handleSendOrderPdfWhatsapp = async () => {
        if (!id) return;
        try {
            setSendingWhatsapp(true);
            await httpClient.post(`/orderservices/${id}/whatsapp/send`, { phone: whatsappPhone });
            setWhatsappDialogOpen(false);
            setMessage('Ordem de serviço enviada por WhatsApp');
            setSeverity('success');
        } catch (error) {
            console.error('Erro ao enviar ordem por WhatsApp:', error);
            const data = error.response?.data;
            setMessage((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao enviar a ordem por WhatsApp');
            setSeverity('error');
        } finally {
            setSendingWhatsapp(false);
            setOpenSnackbar(true);
        }
    };

    const handleCopyOrderLink = async () => {
        const enterpriseId = getEnterpriseIdFromToken();
        if (!orderNumber || !enterpriseId) {
            setMessage('Não foi possível gerar o link da ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/ordem/${orderNumber}?t=${encodeURIComponent(btoa(enterpriseId))}`);
            setMessage('Link da ordem copiado');
            setSeverity('success');
        } catch (error) {
            console.error('Erro ao copiar link da ordem:', error);
            setMessage('Erro ao copiar o link da ordem de serviço');
            setSeverity('error');
        }
        setOpenSnackbar(true);
    };

    const handleReopenOrder = async () => {
        if (reopeningOrder) return;
        setReopeningOrder(true);
        try {
            await httpClient.post(`/orderservices/${id}/reopen`);
            setOpenReopenDialog(false);
            setOrderState(prev => ({ ...prev, status: 'Em Execução' }));
            setMessage('Ordem de serviço reaberta com sucesso');
            setSeverity('success');
        } catch (error) {
            setOpenReopenDialog(false);
            setMessage(messageFromApiErrorBody(error.response?.data) || 'Erro ao reabrir ordem de serviço');
            setSeverity('error');
        } finally {
            setReopeningOrder(false);
            setOpenSnackbar(true);
        }
    };

    const headerActions = [];
    if (isEditableStatus) {
        if (canEditOrder) {
            headerActions.push({ key: 'edit', label: 'Editar ordem', icon: Pencil, onClick: handleEdit });
        }
        if (settings?.os_use_checklist && canFillChecklist) {
            headerActions.push({ key: 'checklist', label: 'Preencher checklist', icon: ClipboardCheck, onClick: handleOpenChecklistModal });
        }
        headerActions.push({ key: 'pdf', label: 'Baixar PDF', icon: Download, onClick: handleDownloadOrderPDF, loading: downloadingOrderPDF, disabled: downloadingOrderPDF });
        if (isNaoIniciada && hasPermission('serviceorder.execute')) {
            headerActions.push({ key: 'execute', label: 'Iniciar execução', icon: Play, onClick: handleExecuteOrder, solid: true });
        }
        if (isEmExecucao) {
            headerActions.push({
                key: 'finish',
                label: 'Finalizar ordem',
                icon: Check,
                solid: true,
                onClick: () => {
                    setOpenDialogConfirm(true);
                    setMessageDialogConfirm('Tem certeza que deseja finalizar esta ordem de serviço?');
                    setConfirmationAction('finalize');
                },
            });
        }
        if (hasPermission('serviceorder.cancel')) {
            headerActions.push({
                key: 'cancel',
                label: 'Cancelar ordem',
                icon: X,
                danger: true,
                onClick: handleOpenCancelDialog,
            });
        }
    } else if (isFinalizada) {
        if (hasPermission('serviceorder.execute')) {
            headerActions.push({ key: 'reopen', label: 'Reabrir', icon: RotateCcw, onClick: () => setOpenReopenDialog(true), loading: reopeningOrder, disabled: reopeningOrder });
        }
        headerActions.push({ key: 'pdf', label: 'Baixar PDF', icon: Download, onClick: handleDownloadOrderPDF, loading: downloadingOrderPDF, disabled: downloadingOrderPDF });
        headerActions.push({ key: 'copy-link', label: 'Copiar link', icon: Link2, onClick: handleCopyOrderLink });
        if (canSendWhatsapp) {
            headerActions.push({ key: 'whatsapp-send', label: 'Enviar por WhatsApp', icon: Send, onClick: handleOpenWhatsappSend, disabled: !whatsappConnected });
        }
        if (hasPermission('serviceorder.bill')) {
            headerActions.push({ key: 'pay', label: 'Faturar ordem', icon: CreditCard, solid: true, onClick: handleOpenPaymentModal });
        }
    } else {
        // Faturada/Paga e Cancelada — estados terminais, só resta consultar.
        headerActions.push({ key: 'pdf', label: 'Baixar PDF', icon: Download, onClick: handleDownloadOrderPDF, loading: downloadingOrderPDF, disabled: downloadingOrderPDF });
    }

    // Mobile (`MobileOrderOperator`): finalizar a partir da folha "Finalizar OS" (sem o diálogo de confirmação).
    const [finishingMobile, setFinishingMobile] = useState(false);
    const handleMobileFinish = async () => {
        setFinishingMobile(true);
        try {
            const finished = await handleFinalizeOrder();
            // Finalizada: o voltar (app bar/sistema) passa a ir para a lista.
            if (finished) navigate(`/serviceorder/${id}/operator`, { replace: true, state: { id, fromFlow: true } });
            return finished;
        } finally {
            setFinishingMobile(false);
        }
    };
    const handleMobileFinishAndBill = async () => {
        const finished = await handleMobileFinish();
        if (finished) handleOpenPaymentModal();
    };

    const orderDatePedido = formatOrderDateTime(orderDetails.order_date);
    const orderDataEntrega = formatOrderDateTime(orderDetails.delivery_date);
    const hasProductItemDiscount = orderState.total_product_item_discount && orderState.total_product_item_discount !== '0,00';

    const filteredCart = orderState.cart.filter((item) => itemFilter === 'todos' || item.type === itemFilter);

    return (
        <>
            <PageScaffold
                title="Ordem de Serviço"
                links={breadcrumbs}
                openSnackbar={isMobileShell ? false : openSnackbar}
                message={message}
                setOpenSnackbar={setOpenSnackbar}
                severity={severity}
            >
                {isMobileShell ? (
                    <MobileOrderOperator
                        loading={loading}
                        orderNumber={orderNumber}
                        status={orderState.status}
                        details={orderDetails}
                        usesVehicle={Boolean(settings?.os_use_vehicle)}
                        checklistEnabled={Boolean(settings?.os_use_checklist)}
                        orderTotal={orderState.total}
                        items={orderState.cart}
                        loadingItems={loadingItems}
                        attention=""
                        finance={financeState}
                        payments={payments}
                        checklist={checklistData}
                        loadingChecklist={loadingChecklist}
                        editable={isEditableStatus}
                        canEdit={canEditOrder}
                        canFillChecklist={canFillChecklist}
                        canExecute={hasPermission('serviceorder.execute')}
                        canCancel={hasPermission('serviceorder.cancel')}
                        canBill={hasPermission('serviceorder.bill')}
                        canSendWhatsapp={hasPermission('serviceorder.send')}
                        printing={downloadingOrderPDF}
                        isItemBusy={(itemId) => isItemLoading(itemId, 'increment') || isItemLoading(itemId, 'decrement') || isItemLoading(itemId, 'delete') || loadingState.isAddingItem}
                        onPrint={handleDownloadOrderPDF}
                        onEdit={handleEdit}
                        onChecklist={handleOpenChecklistModal}
                        onExecute={handleExecuteOrder}
                        onFinish={handleMobileFinish}
                        onFinishAndBill={handleMobileFinishAndBill}
                        finishing={finishingMobile}
                        onBill={handleOpenPaymentModal}
                        onCancel={handleOpenCancelDialog}
                        onStep={(itemId, delta) => (delta > 0 ? handleIncrementItem(itemId) : handleDecrementItem(itemId))}
                        onEditItem={handleOpenEditItemModal}
                        onDiscount={openProductItemDiscountDialog}
                        onRemove={handleDeleteItem}
                        onSearch={handleSearchItem}
                        searchResults={itemsAutocomplete}
                        onAddItem={(item) => addToCart(item, 1)}
                        addingItem={loadingState.isAddingItem}
                        onChecklistPdf={downloadChecklistPDF}
                        downloadingChecklistPdf={downloadingChecklistPDF}
                        onChecklistWhatsapp={() => setChecklistWhatsappOpen(true)}
                    />
                ) : (
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* CABEÇALHO — os-header (gradiente de marca, §2.4/§6.3) */}
                    {loading ? (
                        <Card elevation={0} sx={{ borderRadius: '20px', mb: 2.25, p: '22px 24px', background: (t) => t.palette.gradient.brand, boxShadow: (t) => t.customShadows.md }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.25, flexWrap: 'wrap' }}>
                                <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: 'rgba(255,255,255,.25)' }} />
                                <Skeleton variant="text" width={200} height={34} sx={{ bgcolor: 'rgba(255,255,255,.25)' }} />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 2.25 }}>
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} variant="text" width={90} height={36} sx={{ bgcolor: 'rgba(255,255,255,.2)' }} />
                                ))}
                            </Box>
                        </Card>
                    ) : (
                        <Card elevation={0} sx={{ borderRadius: '20px', mb: 2.25, p: '22px 24px', background: (t) => t.palette.gradient.brand, boxShadow: (t) => t.customShadows.md }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    {!isMobileShell && <Tooltip title="Voltar">
                                        <IconButton
                                            onClick={() => navigate('/serviceorders')}
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: '10px',
                                                bgcolor: 'rgba(255,255,255,.15)',
                                                border: '1px solid rgba(255,255,255,.25)',
                                                color: '#fff',
                                                '&:hover': { bgcolor: 'rgba(255,255,255,.25)' },
                                            }}
                                        >
                                            <ArrowLeft size={16} strokeWidth={2} />
                                        </IconButton>
                                    </Tooltip>}
                                    <Typography component="h1" sx={{ fontSize: '21px', fontWeight: 800, letterSpacing: '-.01em', color: '#fff' }}>
                                        ORDEM Nº {orderNumber}
                                    </Typography>
                                    <HeaderStatusPill status={orderState.status} />
                                </Box>

                                {!isMobileShell && (
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {headerActions.map(({ key, ...action }) => (
                                            <HeaderActionButton key={key} {...action} />
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {!isForm && (
                                <>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                                        gap: 2.25,
                                    }}>
                                        <MetaItem label="Data do pedido" value={orderDatePedido} />
                                        <MetaItem label="Data de entrega" value={orderDataEntrega} />
                                        <MetaItem
                                            label="Cliente"
                                            value={orderDetails.customer_name}
                                            sub={orderDetails.customer_contact ? formatPhone(orderDetails.customer_contact) : null}
                                        />
                                        {settings?.os_use_vehicle && (
                                            <MetaItem
                                                label="Veículo"
                                                value={orderDetails.vehicle_name}
                                                sub={
                                                    orderDetails.vehicle_plate && orderDetails.vehicle_type !== 'bicycle'
                                                        ? `Placa ${orderDetails.vehicle_plate}`
                                                        : null
                                                }
                                            />
                                        )}
                                        <MetaItem label="Motivo/Defeito" value={orderDetails.defect_reason} />
                                        <MetaItem label="Observações" value={orderDetails.observations} />
                                        {isOrderCancelled && (
                                            <MetaItem label="Motivo do cancelamento" value={orderDetails.cancel_reason} />
                                        )}
                                    </Box>

                                    {orderDetails.budget_id && orderDetails.budget_number && (
                                        <Box
                                            component={RouterLink}
                                            to={`/servicequotes/${orderDetails.budget_id}/operator`}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                fontSize: '11.5px',
                                                fontWeight: 700,
                                                padding: '5px 11px',
                                                borderRadius: '20px',
                                                bgcolor: 'rgba(255,255,255,.15)',
                                                color: '#fff',
                                                mt: 1.75,
                                                textDecoration: 'none',
                                                width: 'fit-content',
                                                '&:hover': { bgcolor: 'rgba(255,255,255,.25)' },
                                            }}
                                        >
                                            <FileText size={12} strokeWidth={2} />
                                            Gerada a partir do Orçamento #{orderDetails.budget_number}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Card>
                    )}

                    {/* ABAS — seg-tabs Itens/Financeiro/Checklist */}
                    <ToggleButtonGroup
                        exclusive
                        value={activeTab}
                        onChange={(e, v) => v && setActiveTab(v)}
                        sx={{
                            mb: 2.25,
                            width: isMobileShell ? '100%' : 'fit-content',
                            // Mobile: abas dividem a linha, sem quebra de texto.
                            '& .MuiToggleButton-root': isMobileShell
                                ? { flex: '1 1 0', minWidth: 0, px: 0.75, gap: 0.5, fontSize: 12.5, whiteSpace: 'nowrap', lineHeight: 1.2 }
                                : undefined,
                        }}
                    >
                        <ToggleButton value="itens" sx={{ gap: 0.75, px: 2.25 }}>
                            <Layers size={14} strokeWidth={2} /> {isMobileShell ? 'Itens' : 'Itens da ordem'}
                        </ToggleButton>
                        <ToggleButton value="financeiro" sx={{ gap: 0.75, px: 2.25 }}>
                            <DollarSign size={14} strokeWidth={2} /> Financeiro
                        </ToggleButton>
                        {settings?.os_use_checklist && (
                            <ToggleButton value="checklist" sx={{ gap: 0.75, px: 2.25 }}>
                                <ClipboardCheck size={14} strokeWidth={2} /> Checklist
                            </ToggleButton>
                        )}
                    </ToggleButtonGroup>

                    {/* ABA: ITENS */}
                    {activeTab === 'itens' && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.7fr 1fr' }, gap: 2, alignItems: 'flex-start' }}>
                            <Card elevation={0}>
                                <CardContent sx={{ p: 2 }}>
                                    {isEditableStatus && (
                                        <Box sx={{ mb: 2 }}>
                                            <Collapse in={loadingState.showProgress}>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
                                                            Adicionando: {loadingState.addingItemName}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{loadingState.progress}%</Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={loadingState.progress}
                                                        sx={{
                                                            height: 6,
                                                            borderRadius: 3,
                                                            bgcolor: 'background.subtle',
                                                            '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundImage: (t) => t.palette.gradient.brand },
                                                        }}
                                                    />
                                                </Box>
                                            </Collapse>
                                            <Collapse in={loadingState.showSuccess}>
                                                <Box sx={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 1.5,
                                                    px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: 'success.bg', color: 'success.main',
                                                    fontSize: 11.5, fontWeight: 700,
                                                }}>
                                                    <CheckCircle2 size={13} /> {loadingState.addingItemName} adicionado com sucesso!
                                                </Box>
                                            </Collapse>
                                            <Box sx={{
                                                opacity: loadingState.isAddingItem ? 0.6 : 1,
                                                transition: 'opacity 0.3s ease',
                                                pointerEvents: loadingState.isAddingItem ? 'none' : 'auto',
                                            }}>
                                                <DefaultAutocomplete
                                                    variant="pos"
                                                    autoFocus={uiState.fieldSearchFocus}
                                                    resetKey={uiState.autocompleteResetKey}
                                                    handleGetSelected={handleGetSelected}
                                                    handleChange={(e) => {
                                                        handleSearchItem(e.target.value);
                                                        setUiState(prev => ({ ...prev, searchProduct: e.target.value }));
                                                    }}
                                                    id="product-search"
                                                    placeholder="Buscar produto ou serviço para adicionar…"
                                                    inputValue={uiState.searchProduct}
                                                    options={itemsAutocomplete}
                                                />
                                            </Box>
                                        </Box>
                                    )}

                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={itemFilter}
                                        onChange={(e, v) => v && setItemFilter(v)}
                                        sx={{ mb: 2, ...(isMobileShell && { width: '100%', '& .MuiToggleButton-root': { flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap' } }) }}
                                    >
                                        <ToggleButton value="todos">{isMobileShell ? 'Todos' : 'Todos os itens'}</ToggleButton>
                                        <ToggleButton value="product">{isMobileShell ? 'Produtos' : 'Apenas produtos'}</ToggleButton>
                                        <ToggleButton value="service">{isMobileShell ? 'Serviços' : 'Apenas serviços'}</ToggleButton>
                                    </ToggleButtonGroup>

                                    <Box sx={{ maxHeight: 560, overflowY: 'auto', pr: 0.5 }}>
                                        {loadingItems ? (
                                            <Stack spacing={1}>
                                                {[1, 2, 3].map((n) => (
                                                    <Skeleton key={n} variant="rounded" height={68} sx={{ borderRadius: '12px' }} />
                                                ))}
                                            </Stack>
                                        ) : filteredCart.length === 0 ? (
                                            <EmptyMiniState
                                                icon={Layers}
                                                title="Nenhum item nesta ordem"
                                                subtitle={isEditableStatus ? 'Busque um produto ou serviço acima para adicionar' : 'Esta ordem não possui itens registrados'}
                                            />
                                        ) : (
                                            filteredCart.map((item) => (
                                                <CartRow
                                                    key={item.id}
                                                    item={item}
                                                    editable={isEditableStatus}
                                                    incrementing={isItemLoading(item.id, 'increment') || loadingState.isAddingItem}
                                                    decrementing={isItemLoading(item.id, 'decrement') || loadingState.isAddingItem}
                                                    deleting={isItemLoading(item.id, 'delete')}
                                                    onIncrement={() => handleIncrementItem(item.id)}
                                                    onDecrement={() => handleDecrementItem(item.id)}
                                                    onEdit={() => handleOpenEditItemModal(item)}
                                                    onDiscount={() => openProductItemDiscountDialog(item)}
                                                    onRemove={() => handleDeleteItem(item.id)}
                                                />
                                            ))
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card elevation={0} sx={{ position: 'sticky', top: 20 }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Resumo da ordem</Typography>
                                    {loadingValues ? (
                                        <Stack spacing={1}>
                                            {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="text" height={28} />)}
                                        </Stack>
                                    ) : (
                                        <>
                                            <SummaryRow label="Total de itens" value={orderState.totalItems} />
                                            <SummaryRow label="Produtos" value={`R$ ${orderState.total_products}`} />
                                            {hasProductItemDiscount && (
                                                <SummaryRow label="Desconto nos produtos" value={`− R$ ${orderState.total_product_item_discount}`} tone="danger" />
                                            )}
                                            <SummaryRow label="Serviços" value={`R$ ${orderState.total_services}`} />
                                            <Divider sx={{ my: 1 }} />
                                            <SummaryRow label="Total" value={`R$ ${orderState.total}`} tone="success" big />
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {/* ABA: FINANCEIRO */}
                    {activeTab === 'financeiro' && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            <Card elevation={0}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Resumo financeiro</Typography>
                                    {loadingFinance ? (
                                        <Stack spacing={1}>
                                            {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="text" height={28} />)}
                                        </Stack>
                                    ) : (
                                        <>
                                            <SummaryRow label="Subtotal" value={`R$ ${financeState.subtotal}`} />
                                            <SummaryRow label="Desconto" value={`− R$ ${financeState.discount}`} tone="danger" />
                                            <SummaryRow label="Total" value={`R$ ${financeState.total}`} />
                                            <Divider sx={{ my: 1 }} />
                                            <SummaryRow label="Total líquido" value={`R$ ${financeState.total_liquid}`} tone="success" big />
                                        </>
                                    )}
                                </CardContent>
                            </Card>

                            <Card elevation={0}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Formas de pagamento</Typography>
                                    {loadingPayments ? (
                                        <Stack spacing={1}>
                                            {[1, 2].map((n) => <Skeleton key={n} variant="rounded" height={62} sx={{ borderRadius: '12px' }} />)}
                                        </Stack>
                                    ) : payments.length === 0 ? (
                                        <EmptyMiniState
                                            icon={CreditCard}
                                            title="Nenhum pagamento registrado ainda"
                                            subtitle={isFinalizada ? 'Clique em "Faturar ordem" no topo para registrar.' : 'Finalize a ordem primeiro.'}
                                        />
                                    ) : (
                                        <Box>
                                            {payments.map((payment, index) => (
                                                <PaymentRow key={index} payment={payment} />
                                            ))}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {/* ABA: CHECKLIST */}
                    {activeTab === 'checklist' && settings?.os_use_checklist && (
                        <Box>
                            {loadingChecklist ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : checklistData ? (
                                <Card elevation={0}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{checklistData.checklist_name}</Typography>
                                                <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>
                                                    {checklistData.mechanic_name && <>Preenchido por: <b>{checklistData.mechanic_name}</b></>}
                                                    {checklistData.completion_date && ` · ${new Date(checklistData.completion_date).toLocaleDateString('pt-BR')}`}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={downloadingChecklistPDF ? <CircularProgress size={13} /> : <Download size={14} strokeWidth={2} />}
                                                    onClick={downloadChecklistPDF}
                                                    disabled={downloadingChecklistPDF}
                                                >
                                                    Baixar PDF
                                                </Button>
                                                {hasPermission('serviceorder.send') && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<Send size={14} strokeWidth={2} />}
                                                        onClick={() => setChecklistWhatsappOpen(true)}
                                                    >
                                                        Enviar por WhatsApp
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>

                                        {checklistData.evaluations && checklistData.evaluations.length > 0 ? (
                                            Object.values(
                                                checklistData.evaluations.reduce((acc, evaluation) => {
                                                    const groupId = evaluation.group_id || 'other';
                                                    if (!acc[groupId]) {
                                                        acc[groupId] = { group_id: groupId, group_name: evaluation.group_name || 'Outros', evaluations: [] };
                                                    }
                                                    acc[groupId].evaluations.push(evaluation);
                                                    return acc;
                                                }, {})
                                            ).map((group) => (
                                                <Box key={group.group_id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px', mb: 1.75, overflow: 'hidden' }}>
                                                    <Box sx={{
                                                        p: '13px 16px',
                                                        background: (t) => t.palette.gradient.brand,
                                                        color: '#fff',
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}>
                                                        <ClipboardCheck size={14} strokeWidth={2} /> {group.group_name}
                                                    </Box>
                                                    <Box>
                                                        {group.evaluations.map((evaluation, index) => (
                                                            <Box
                                                                key={evaluation.evaluation_id || index}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1.5,
                                                                    p: '12px 16px',
                                                                    borderBottom: index < group.evaluations.length - 1 ? '1px solid' : 'none',
                                                                    borderColor: 'divider',
                                                                    flexWrap: 'wrap',
                                                                }}
                                                            >
                                                                <Typography sx={{ flex: 1, minWidth: 160, fontSize: 13, fontWeight: 600 }}>
                                                                    {evaluation.item_name}
                                                                </Typography>
                                                                <StatusChip domain="checklist" value={evaluation.answer_description} />
                                                                {evaluation.observation && (
                                                                    <Typography sx={{ width: '100%', fontSize: 11.5, fontStyle: 'italic', color: 'text.secondary', borderLeft: '2px solid', borderColor: 'divider', pl: 1.25 }}>
                                                                        {evaluation.observation}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', py: 3 }}>
                                                Nenhuma avaliação encontrada
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            ) : (
                                <Card elevation={0}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <EmptyMiniState
                                            icon={ClipboardCheck}
                                            title="Nenhum checklist preenchido"
                                            subtitle="Preencha uma inspeção do veículo para este atendimento"
                                            size={70}
                                            action={
                                                isEditableStatus && canFillChecklist ? (
                                                    <Button variant="contained" size="small" onClick={handleOpenChecklistModal} sx={{ mt: 1 }}>
                                                        Preencher checklist
                                                    </Button>
                                                ) : null
                                            }
                                        />
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                    )}
                </Box>

                )}

                {/* DRAWER: editar dados da ordem */}
                <Drawer
                    anchor="right"
                    open={isForm}
                    onClose={closeOrderDataSheet}
                    slotProps={{
                        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } },
                    }}
                    PaperProps={{
                        sx: {
                            width: { xs: '100%', sm: 560, md: 640 },
                            maxWidth: '100vw',
                            borderTopLeftRadius: 16,
                            borderBottomLeftRadius: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Editar dados da ordem</Typography>
                        <IconButton onClick={closeOrderDataSheet} size="small">
                            <X size={16} strokeWidth={2} />
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {isForm && (
                            <OrderData
                                ref={orderDataRef}
                                isForm={isForm}
                                setMessage={setMessage}
                                setOpenSnackbar={setOpenSnackbar}
                                editMode={edit}
                                handleSubmit={handleSubmit}
                                id={orderId || id}
                                setOrderNumber={setOrderNumber}
                                wasUpdated={wasUpdated}
                                status={orderState.status}
                                setOrderStatus={(status) => {
                                    if (status && status !== orderState.status) {
                                        setOrderState(prev => ({ ...prev, status: status }));
                                    }
                                }}
                                onOrderDataChange={(data) => {
                                    if (data) {
                                        setOrderDetails((prev) => ({
                                            ...prev,
                                            customer_name: data.customer_name || '',
                                            customer_contact: data.customer_contact || '',
                                            vehicle_name: data.vehicle_name || '',
                                            vehicle_plate: data.vehicle_plate || '',
                                            vehicle_type: data.vehicle_type || '',
                                            order_date: data.order_date || null,
                                            delivery_date: data.delivery_date || null,
                                            observations: data.observations || '',
                                            defect_reason: data.defect_reason || '',
                                        }));
                                    }
                                }}
                            />
                        )}
                    </Box>

                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Button variant="outlined" onClick={closeOrderDataSheet}>Cancelar</Button>
                        <Button variant="contained" startIcon={<Save size={15} strokeWidth={2} />} onClick={handleSave}>Salvar</Button>
                    </Box>
                </Drawer>

                <DialogForm
                    open={openDialogConfirm}
                    iconType="warning"
                    onClose={() => {
                        setOpenDialogConfirm(loadingConfirmAction);
                        setConfirmationAction(null);
                    }}
                    onSubmit={handleConfirmAction}
                    title="Atenção"
                    content={messageDialogConfirm}
                    confirmButtonText="Confirmar"
                    cancelButtonText="Cancelar"
                    loading={loadingConfirmAction}
                    loadingText="Processando..."
                />

                <DialogForm
                    open={openCancelDialog}
                    iconType="warning"
                    onClose={() => {
                        if (!loadingCancel) setOpenCancelDialog(false);
                    }}
                    onSubmit={handleCancelOrder}
                    title="Atenção"
                    content="Tem certeza que deseja cancelar esta ordem de serviço?"
                    secondaryContent="Informe o motivo do cancelamento."
                    confirmButtonText="Sim, Cancelar"
                    cancelButtonText="Voltar"
                    loading={loadingCancel}
                    loadingText="Cancelando..."
                >
                    <TextField
                        sx={standaloneFieldSx}
                        label="Motivo"
                        fullWidth
                        multiline
                        minRows={3}
                        required
                        autoFocus
                        value={cancelReason}
                        onChange={(e) => {
                            setCancelReason(e.target.value);
                            if (cancelReasonError) setCancelReasonError('');
                        }}
                        error={Boolean(cancelReasonError)}
                        helperText={cancelReasonError}
                        disabled={loadingCancel}
                    />
                </DialogForm>

                {/* DRAWER: faturamento */}
                <PaymentModal
                    open={openPaymentModal}
                    onClose={handleClosePaymentModal}
                    total={orderState.total}
                    onFinish={handlePaymentFinish}
                    entityType="service_order"
                    entityId={id}
                />

                {/* Modal de Edição de Item */}
                <DialogForm
                    open={openEditItemModal}
                    iconType="edit"
                    onClose={handleCloseEditItemModal}
                    onSubmit={handleSaveItem}
                    title="Editar Item"
                    content={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <TextField
                                label="Nome do Item"
                                value={itemName}
                                onChange={(e) => setItemName(e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                                sx={standaloneFieldSx}
                                disabled={editingItem && !editingItem.is_generic}
                                helperText={editingItem && !editingItem.is_generic ? "Nome não pode ser alterado para itens não genéricos" : ""}
                            />
                            <TextField
                                label="Valor"
                                type="text"
                                value={itemValue}
                                onChange={(e) => setItemValue(e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                                sx={standaloneFieldSx}
                                disabled={editingItem && !editingItem.is_generic}
                                inputProps={{ step: "0.01", min: "0" }}
                                helperText={editingItem && !editingItem.is_generic ? "Valor não pode ser alterado para itens não genéricos" : ""}
                            />
                            <TextField
                                label="Quantidade"
                                type="number"
                                value={itemQuantity}
                                onChange={(e) => setItemQuantity(e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                                sx={standaloneFieldSx}
                                inputProps={{ min: "1" }}
                            />
                            {editingItem && editingItem.type === 'product' && (
                                <TextField
                                    label="Desconto no produto (linha)"
                                    fullWidth
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                    placeholder="10% ou 10,00"
                                    value={editItemDiscountValue}
                                    onChange={(e) => setEditItemDiscountValue(e.target.value)}
                                    helperText="Opcional. Vazio mantém o desconto atual. Para remover, use o botão de desconto (ícone) na linha."
                                />
                            )}
                        </Box>
                    }
                    confirmButtonText="Salvar"
                    cancelButtonText="Cancelar"
                />

                <DialogForm
                    iconType="info"
                    title="Desconto no produto"
                    subtitle={itemDiscountDialog.item?.name || itemDiscountDialog.item?.item_name || ''}
                    content="Percentual (ex.: 10%) ou valor fixo (ex.: 10,00). Diferente do desconto global na ordem. Use Remover para limpar o desconto da linha."
                    open={itemDiscountDialog.open}
                    onClose={() => {
                        if (!itemDiscountDialog.loading) {
                            setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
                        }
                    }}
                    onCancel={() => {
                        if (!itemDiscountDialog.loading) {
                            setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
                        }
                    }}
                    onSubmit={() => handleSubmitProductItemDiscount(false)}
                    confirmButtonText="Aplicar"
                    cancelButtonText="Cancelar"
                    loading={itemDiscountDialog.loading}
                    loadingText="Aplicando..."
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Desconto"
                            fullWidth
                            variant="outlined"
                            sx={standaloneFieldSx}
                            placeholder="10% ou 10,00"
                            disabled={itemDiscountDialog.loading}
                            value={itemDiscountDialog.value}
                            onChange={(e) =>
                                setItemDiscountDialog((prev) => ({ ...prev, value: e.target.value }))
                            }
                        />
                        <Button
                            type="button"
                            variant="text"
                            color="error"
                            size="small"
                            disabled={itemDiscountDialog.loading}
                            onClick={() => handleSubmitProductItemDiscount(true)}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                        >
                            Remover desconto
                        </Button>
                    </Box>
                </DialogForm>

                {/* Modal de Seleção de Checklist */}
                {settings?.os_use_checklist && (
                    <ChecklistSelectionModal
                        open={openChecklistSelectionModal}
                        onClose={() => setOpenChecklistSelectionModal(false)}
                        onSelectChecklist={handleSelectChecklist}
                        serviceOrderId={id}
                    />
                )}

                {/* Modal de Preenchimento de Checklist */}
                {settings?.os_use_checklist && (
                    <ChecklistFillerModal
                        open={openChecklistFillerModal}
                        onClose={handleChecklistFillerClose}
                        checklistId={selectedChecklist?.id}
                        serviceOrderId={id}
                        onSuccess={handleChecklistFillerSuccess}
                        onMessage={(msg, msgSeverity) => {
                            setMessage(msg);
                            setSeverity(msgSeverity);
                            setOpenSnackbar(true);
                        }}
                    />
                )}

                <DialogForm
                    iconType="warning"
                    open={openReopenDialog}
                    title="Atenção"
                    confirmButtonText="Sim, Reabrir"
                    content="Tem certeza que deseja reabrir a ordem de serviço? Ela voltará para o status Em Execução."
                    onClose={() => { if (!reopeningOrder) setOpenReopenDialog(false); }}
                    onSubmit={handleReopenOrder}
                    loading={reopeningOrder}
                    loadingText="Reabrindo..."
                />

                {/* MODAL: conferir/editar o número antes de enviar o PDF por WhatsApp */}
                <Dialog open={whatsappDialogOpen} onClose={() => !sendingWhatsapp && setWhatsappDialogOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle>Enviar por WhatsApp</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                            O PDF da ordem nº {orderNumber} será enviado ao número abaixo. Confira antes de enviar.
                        </Typography>
                        <TextField
                            label="Telefone (WhatsApp)"
                            helperText={loadingWhatsappPhone ? 'Buscando celular do cliente...' : 'Celular do cliente (editável). DDD + número, ex.: (11) 99999-9999'}
                            fullWidth
                            autoFocus
                            sx={standaloneFieldSx}
                            value={whatsappPhone}
                            onChange={(e) => setWhatsappPhone(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setWhatsappDialogOpen(false)} disabled={sendingWhatsapp}>Cancelar</Button>
                        <Button
                            variant="contained"
                            onClick={handleSendOrderPdfWhatsapp}
                            disabled={sendingWhatsapp || !whatsappPhone.trim()}
                            startIcon={sendingWhatsapp ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <Send size={14} strokeWidth={2} />}
                        >
                            Enviar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Modal: enviar o PDF do checklist por WhatsApp */}
                {hasPermission('serviceorder.send') && (
                    <ChecklistWhatsappDialog
                        open={checklistWhatsappOpen}
                        serviceOrderId={id}
                        orderNumber={orderNumber}
                        onClose={() => setChecklistWhatsappOpen(false)}
                        onResult={(msg, msgSeverity) => {
                            setMessage(msg);
                            setSeverity(msgSeverity);
                            setOpenSnackbar(true);
                        }}
                    />
                )}

                {/* Mobile: erros do operar/faturar OS (antes toast/banner) em modal único */}
                {isMobileShell && (
                    <MobileAlertDialog open={mobileAlert.open} message={mobileAlert.message} onClose={closeMobileAlert} />
                )}
            </PageScaffold>
        </>
    );
}
